'use client';

import { Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { LongConfigValues } from './longConfig.schema';
import { dayOfWeekEnum, dayOfWeekMap } from '@/components/clubs/upsert-club/validations/validations';

export interface LongConfigBlockProps {
  values: Partial<LongConfigValues>;
  onChange: (patch: Partial<LongConfigValues>) => void;
  /** Deadline dictated by a weekly grid shared with other tournaments; the fields are read-only then. */
  lockedDeadline?: { day: LongConfigValues['availabilityDeadlineDay']; time: string } | null;
}

const DAYS_OF_WEEK = dayOfWeekEnum.options.map((value) => ({ value, label: dayOfWeekMap[value] }));

/** Weekly configuration block for LONG tournaments. */
export function LongConfigBlock({ values, onChange, lockedDeadline }: LongConfigBlockProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Settings className="h-5 w-5 text-blue-600" />
          Configuración semanal
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Parámetros de las jornadas semanales del torneo largo.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Availability deadline */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="long-deadline-day">Día límite para cargar disponibilidad *</Label>
            <Select
              value={lockedDeadline?.day ?? values.availabilityDeadlineDay ?? ''}
              disabled={Boolean(lockedDeadline)}
              onValueChange={(v) =>
                onChange({ availabilityDeadlineDay: v as LongConfigValues['availabilityDeadlineDay'] })
              }
            >
              <SelectTrigger id="long-deadline-day">
                <SelectValue placeholder="Seleccionar día" />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OF_WEEK.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Día de la semana hasta el que los equipos pueden cargar su disponibilidad.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="long-deadline-time">Hora límite *</Label>
            <Input
              id="long-deadline-time"
              type="time"
              value={lockedDeadline?.time ?? values.availabilityDeadlineTime ?? ''}
              onChange={(e) => onChange({ availabilityDeadlineTime: e.target.value })}
              disabled={Boolean(lockedDeadline)}
            />
            <p className="text-xs text-muted-foreground">Hora del día de cierre (HH:mm).</p>
          </div>
        </div>
        {lockedDeadline && (
          <p className="text-sm text-blue-700">
            El cierre lo define la grilla semanal elegida en Clubs y Horarios: la comparten otros torneos
            y todos cierran la carga de disponibilidad el mismo día y a la misma hora.
          </p>
        )}

        {/* Numeric config */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="long-min-schedules">Mínimo de horarios requeridos *</Label>
            <Input
              id="long-min-schedules"
              type="number"
              min={1}
              value={values.minSchedulesRequired ?? ''}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onChange({ minSchedulesRequired: Number.isNaN(n) ? undefined : n });
              }}
            />
            <p className="text-xs text-muted-foreground">
              Cantidad mínima de horarios que debe cargar cada equipo.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="long-walkovers">Walkovers para descalificación *</Label>
            <Input
              id="long-walkovers"
              type="number"
              min={1}
              value={values.walkoversForDisqualification ?? ''}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                onChange({ walkoversForDisqualification: Number.isNaN(n) ? undefined : n });
              }}
            />
            <p className="text-xs text-muted-foreground">
              Cantidad de walkovers acumulados que descalifica a un equipo.
            </p>
          </div>
        </div>

        {/* Auto-publicación */}
        <div className="flex items-center justify-between rounded-lg border p-4">
          <div className="space-y-0.5">
            <Label htmlFor="long-auto-publish" className="text-base font-medium">
              Publicación automática de jornadas
            </Label>
            <p className="text-sm text-muted-foreground">
              Las jornadas se publican automáticamente cuando vence el deadline de disponibilidad.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="w-6 text-sm font-medium text-muted-foreground">
              {values.autoPublishRounds ? 'Sí' : 'No'}
            </span>
            <Switch
              id="long-auto-publish"
              checked={values.autoPublishRounds ?? false}
              onCheckedChange={(checked) => onChange({ autoPublishRounds: checked })}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
